// A viewport-sized overlay between the map image and fog. Fog opacity controls
// grid visibility: translucent in the editor and opaque on the player screen.
export class GridOverlay {
  constructor(controller) {
    this.controller = controller;
    this.fog = controller._fogLayer.konvaLayer;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-map-grid';
    this.canvas.setAttribute('aria-hidden', 'true');
    Object.assign(this.canvas.style, { position: 'absolute', top: '0', left: '0', pointerEvents: 'none' });
    controller._imageLayer.getCanvas()._canvas.after(this.canvas);
    this.onDraw = () => this.render();
    this.fog.on('draw.gridOverlay', this.onDraw);
  }

  render() {
    const map = this.controller;
    const source = this.fog.getCanvas()._canvas;
    const ratio = this.fog.getCanvas().getPixelRatio();
    const canvas = this.canvas;
    if (canvas.width !== source.width) canvas.width = source.width;
    if (canvas.height !== source.height) canvas.height = source.height;
    canvas.style.width = source.style.width;
    canvas.style.height = source.style.height;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (
      !map.gridEnabledValue ||
      map._gridCalibration ||
      map.gridHiddenValue ||
      map.gridWidthValue <= 0 ||
      !Number.isFinite(map.gridSizeValue) ||
      map.gridSizeValue < 1
    )
      return;

    const stage = map._stage;
    const scale = stage.scaleX();
    const x = stage.x();
    const y = stage.y();
    const size = map.gridSizeValue;
    const offsetX = map.gridOffsetXValue;
    const offsetY = map.gridOffsetYValue;
    // Only iterate visible lines, even for very large maps at high zoom.
    const rotation = stage.rotation?.() || 0;
    const inverse = rotation ? stage.getAbsoluteTransform().copy().invert() : null;
    const corners =
      inverse &&
      [
        { x: 0, y: 0 },
        { x: stage.width(), y: 0 },
        { x: 0, y: stage.height() },
        { x: stage.width(), y: stage.height() }
      ].map((point) => inverse.point(point));
    const left = Math.max(0, corners ? Math.min(...corners.map((point) => point.x)) : -x / scale);
    const top = Math.max(0, corners ? Math.min(...corners.map((point) => point.y)) : -y / scale);
    const right = Math.min(
      map._imgWidth,
      corners ? Math.max(...corners.map((point) => point.x)) : (stage.width() - x) / scale
    );
    const bottom = Math.min(
      map._imgHeight,
      corners ? Math.max(...corners.map((point) => point.y)) : (stage.height() - y) / scale
    );
    if (right <= left || bottom <= top) return;
    context.save();
    context.scale(ratio, ratio);
    context.translate(x, y);
    if (rotation) context.rotate((rotation * Math.PI) / 180);
    context.scale(scale, scale);
    context.beginPath();
    // Keep the clip inside the image edge: two antialiased edges (grid clip
    // and fog coverage) would otherwise leave a faint rim on fully fogged maps.
    const inset = 1 / (ratio * scale);
    context.rect(inset, inset, Math.max(0, map._imgWidth - inset * 2), Math.max(0, map._imgHeight - inset * 2));
    context.clip();
    context.strokeStyle = map.gridColorValue;
    context.lineWidth = map.gridWidthValue;
    if (map.gridWidthValue >= size) {
      // Repeating bands overlap into a solid surface. Drawing it once keeps
      // work independent of width, including previously saved extreme widths.
      context.fillStyle = map.gridColorValue;
      context.fillRect(left, top, right - left, bottom - top);
    } else {
      context.beginPath();
      const margin = map.gridWidthValue / 2;
      for (let i = Math.ceil((left - margin - offsetX) / size); offsetX + i * size <= right + margin; i++) {
        context.moveTo(offsetX + i * size, top);
        context.lineTo(offsetX + i * size, bottom);
      }
      for (let i = Math.ceil((top - margin - offsetY) / size); offsetY + i * size <= bottom + margin; i++) {
        context.moveTo(left, offsetY + i * size);
        context.lineTo(right, offsetY + i * size);
      }
      context.stroke();
    }
    context.restore();
  }

  destroy() {
    this.fog.off('draw.gridOverlay', this.onDraw);
    this.canvas.remove();
  }
}
